import { useState, useMemo, useEffect } from 'react';
import { useSalesData } from '../../hooks/useSalesData';
import { collection, getDocs, query, where, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from '../../firebase';

const fmtCOP = (value) =>
  Number(value || 0).toLocaleString('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
  });

const toMapById = (arr = []) => new Map(arr.map((p) => [p.id, p]));
const toMapByName = (arr = []) =>
  new Map(arr.map((p) => [String(p.name || '').toLowerCase(), p]));

const BUSINESS_START_HOUR = 8; // 8:00 AM inicio del día de negocio
const BUSINESS_START_MINUTE = 0;
const BUSINESS_END_HOUR = 7; // 7:50 AM del día siguiente fin del día de negocio
const BUSINESS_END_MINUTE = 50;
const FIRESTORE_PAGE_SIZE = 500;

async function fetchAllQueryDocs(buildQuery, pageSize = FIRESTORE_PAGE_SIZE) {
  const docs = [];
  let cursor = null;
  const safePageSize = Math.max(1, Math.min(Number(pageSize) || FIRESTORE_PAGE_SIZE, 1000));

  while (true) {
    const snap = await getDocs(buildQuery(cursor, safePageSize));
    docs.push(...snap.docs);

    if (snap.empty || snap.size < safePageSize) break;

    const nextCursor = snap.docs[snap.docs.length - 1];
    if (!nextCursor || nextCursor.id === cursor?.id) break;
    cursor = nextCursor;
  }

  return docs;
}

const toDate = (v) => {
  // Manejo especial para strings YYYY-MM-DD: llévalas al inicio del día de negocio
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return new Date(
        `${trimmed}T${String(BUSINESS_START_HOUR).padStart(2, '0')}:${String(BUSINESS_START_MINUTE).padStart(2, '0')}:00`
      );
    }
  }
  return v?.toDate ? v.toDate() : new Date(v);
};
const toDateOrNull = (v) => {
  const d = toDate(v);
  return isNaN(d) ? null : d;
};

const addDays = (d, days) => {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + days);
  return copy;
};

// Devuelve el inicio del día comercial (12:00 PM del ymd seleccionado)
const businessDayStart = (ymd) => {
  if (!ymd) return null;
  const base = new Date(`${ymd}T00:00:00`);
  if (isNaN(base)) return null;
  base.setHours(BUSINESS_START_HOUR, BUSINESS_START_MINUTE, 0, 0);
  return base;
};

// Devuelve el fin del día comercial (1:00 AM del día siguiente al ymd seleccionado)
const businessDayEnd = (ymd) => {
  if (!ymd) return null;
  const base = new Date(`${ymd}T00:00:00`);
  if (isNaN(base)) return null;
  const next = addDays(base, 1);
  next.setHours(BUSINESS_END_HOUR, BUSINESS_END_MINUTE, 0, 0);
  return next;
};

function computeLineCost(line, productsById, productsByName) {
  const qty = Number(line.quantity || line.qty || 0);

  if (Array.isArray(line.recipe) && line.recipe.length) {
    let perUnit = 0;
    for (const comp of line.recipe) {
      const compId = comp.insumoId ?? comp.productId ?? comp.id;
      const byId = compId && productsById.get(compId);
      const byName =
        comp?.name && productsByName.get(String(comp.name).toLowerCase());
      const unitCost = Number(byId?.costPrice ?? byName?.costPrice ?? comp.costPrice ?? 0);
      const compQty = Number(comp.qtyPerUnit ?? comp.qty ?? 0);
      perUnit += unitCost * compQty;
    }
    return perUnit * qty;
  }

  const prodId = line.productId ?? line.id;
  const prodName = String(line.name || '').toLowerCase();
  const base =
    productsById.get(prodId)?.costPrice ??
    productsByName.get(prodName)?.costPrice ??
    line.costPrice ??
    0;
  return Number(base) * qty;
}

const Hint = ({ text }) => (
  <span className="ml-2 text-gray-400 cursor-help select-none" title={text} aria-label="info">
    i
  </span>
);

const StatusPill = ({ label = '—', color = 'gray' }) => {
  const palette = {
    green: 'bg-green-100 text-green-700',
    amber: 'bg-amber-100 text-amber-700',
    red: 'bg-red-100 text-red-700',
    gray: 'bg-gray-100 text-gray-700',
  };
  return (
    <span
      className={`mt-1 inline-block text-xs font-semibold px-2 py-1 rounded-full ${palette[color] || palette.gray}`}
    >
      {label}
    </span>
  );
};

const normalizePaid = (value) => {
  const text = String(value || '').toLowerCase();
  if (!text) return null;
  if (text.includes('pag')) return 'paid';
  if (text.includes('pend')) return 'pending';
  if (text.includes('complet')) return 'paid';
  return null;
};

export default function FinancialPanel({
  onBack,
  salesData = [],
  incomingData = [],
  expensesData = [],
  productsData = [],
  outgoingData = [],
  productsLoading = false,
  productsFullReady = true,
}) {
  const SALES_CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 horas
  const [cachedSales, setCachedSales] = useState([]);
  const [incomingRemote, setIncomingRemote] = useState([]);
  const [expensesRemote, setExpensesRemote] = useState([]);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteError, setRemoteError] = useState(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, []);

  const todayLocal = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
  const [filterType, setFilterType] = useState('today');
  const [specificDate, setSpecificDate] = useState(todayLocal);
  const [rangeStart, setRangeStart] = useState(todayLocal);
  const [rangeEnd, setRangeEnd] = useState(todayLocal);

  const { startDate, endDate } = useMemo(() => {
    if (filterType === 'today') return { startDate: todayLocal, endDate: todayLocal };
    if (filterType === 'specific') return { startDate: specificDate, endDate: specificDate };
    return { startDate: rangeStart, endDate: rangeEnd };
  }, [filterType, specificDate, rangeStart, rangeEnd, todayLocal]);

  const { startTs, endTs } = useMemo(() => {
    const start = businessDayStart(startDate);
    const end = businessDayEnd(endDate);
    return { startTs: start, endTs: end };
  }, [startDate, endDate]);

  // Hidrata ventas desde cache local si coincide el rango
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const key = `finpanel:v2:sales:${startDate}:${endDate}`;
      const raw = localStorage.getItem(key);
      if (!raw) {
        setCachedSales([]);
        return;
      }
      const { ts, data } = JSON.parse(raw);
      if (!ts || !Array.isArray(data)) {
        setCachedSales([]);
        return;
      }
      const fresh = Date.now() - ts < SALES_CACHE_TTL_MS;
      setCachedSales(fresh ? data : []);
    } catch {
      setCachedSales([]);
    }
  }, [startDate, endDate]);

  const { salesData: sharedSalesData, loading: salesLoading, error: salesError } = useSalesData({
    startDate,
    endDate,
    max: 500, // tamaño de página; useSalesData carga todas las páginas
  });
  // Prioridad: datos pasados por props > hook > cache local
  const dataSource =
    salesData.length ? salesData :
    (sharedSalesData.length ? sharedSalesData : cachedSales);

  // Si no vienen por props, usa datos remotos cargados aquí
  const incomingSource = incomingData.length ? incomingData : incomingRemote;
  const expensesSource = expensesData.length ? expensesData : expensesRemote;

  // Guarda en cache local cuando llegan ventas nuevas del hook
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!sharedSalesData || !sharedSalesData.length) return;
    try {
      const key = `finpanel:v2:sales:${startDate}:${endDate}`;
      localStorage.setItem(
        key,
        JSON.stringify({ ts: Date.now(), data: sharedSalesData })
      );
    } catch {}
  }, [sharedSalesData, startDate, endDate]);

  const isWithinBusinessRange = (value) => {
    const d = toDateOrNull(value);
    if (!d || !startTs || !endTs) return false;
    return d >= startTs && d < endTs;
  };

  const rangeDays = useMemo(() => {
    if (!startTs || !endTs) return 1;
    const diff = Math.ceil((endTs - startTs) / 86400000);
    return Math.max(diff, 1);
  }, [startTs, endTs]);

  // Fetch de entradas y gastos si no vienen por props.
  // Se pagina hasta completar TODO el rango; 500 es solo tamaño de página.
  useEffect(() => {
    let mounted = true;

    const fetchIE = async () => {
      setRemoteLoading(true);
      setRemoteError(null);

      try {
        const endStrForDateField =
          endTs instanceof Date && !isNaN(endTs)
            ? endTs.toISOString().slice(0, 10)
            : endDate;
        const startIsoTs =
          startTs instanceof Date && !isNaN(startTs)
            ? startTs.toISOString()
            : `${startDate}T00:00:00.000Z`;
        const endIsoTs =
          endTs instanceof Date && !isNaN(endTs)
            ? endTs.toISOString()
            : `${endDate}T23:59:59.999Z`;

        const entriesPromise = fetchAllQueryDocs((cursor, pageSize) => {
          let qEntries = query(
            collection(db, 'entradas'),
            where('registrationDate', '>=', startDate),
            where('registrationDate', '<=', endStrForDateField),
            orderBy('registrationDate', 'asc'),
            limit(pageSize)
          );
          if (cursor) qEntries = query(qEntries, startAfter(cursor));
          return qEntries;
        });

        const expensesByStrPromise = fetchAllQueryDocs((cursor, pageSize) => {
          let qExpenses = query(
            collection(db, 'gastos'),
            where('dateStr', '>=', startDate),
            where('dateStr', '<=', endStrForDateField),
            orderBy('dateStr', 'asc'),
            limit(pageSize)
          );
          if (cursor) qExpenses = query(qExpenses, startAfter(cursor));
          return qExpenses;
        });

        const expensesByDatePromise = fetchAllQueryDocs((cursor, pageSize) => {
          let qExpenses = query(
            collection(db, 'gastos'),
            where('date', '>=', startIsoTs),
            where('date', '<=', endIsoTs),
            orderBy('date', 'asc'),
            limit(pageSize)
          );
          if (cursor) qExpenses = query(qExpenses, startAfter(cursor));
          return qExpenses;
        });

        const [entryDocs, expenseDocsStr, expenseDocsDate] = await Promise.all([
          entriesPromise,
          expensesByStrPromise,
          expensesByDatePromise,
        ]);

        if (!mounted) return;

        setIncomingRemote(entryDocs.map((d) => ({ id: d.id, ...d.data() })));

        // Merge y dedupe gastos por id porque algunos documentos aparecen en
        // ambas estrategias (dateStr y date).
        const expMap = new Map();
        expenseDocsStr.forEach((d) => expMap.set(d.id, { id: d.id, ...d.data() }));
        expenseDocsDate.forEach((d) => expMap.set(d.id, { id: d.id, ...d.data() }));
        setExpensesRemote(Array.from(expMap.values()));
      } catch (e) {
        console.error('FinancialPanel: no se pudo cargar el rango completo', e);
        if (mounted) {
          setIncomingRemote([]);
          setExpensesRemote([]);
          setRemoteError(e);
        }
      } finally {
        if (mounted) setRemoteLoading(false);
      }
    };

    if (!incomingData.length || !expensesData.length) {
      fetchIE();
    } else {
      setIncomingRemote([]);
      setExpensesRemote([]);
      setRemoteError(null);
      setRemoteLoading(false);
    }

    return () => {
      mounted = false;
    };
  }, [startDate, endDate, startTs, endTs, incomingData.length, expensesData.length]);

  const panelLoading = salesLoading || remoteLoading || productsLoading;
  const panelError = salesError || remoteError;
  const catalogIncomplete = productsFullReady === false;

  const productsById = useMemo(() => toMapById(productsData), [productsData]);
  const productsByName = useMemo(() => toMapByName(productsData), [productsData]);

  const filteredSales = useMemo(() => {
    return (dataSource || []).filter((sale) =>
      isWithinBusinessRange(
        sale.date || sale.createdAt || sale.updatedAt || sale.fecha || sale.dateKey
      )
    );
  }, [dataSource, isWithinBusinessRange]);

  const expenseRef = (exp) => {
    // Si afecta caja: prioriza la hora real del turno; si vino a medianoche (módulo Gastos marcado como afecta caja),
    // usa dateStr para anclarlo al inicio del día comercial.
    if (exp?.affectsCash) {
      const d = exp?.date ? toDateOrNull(exp.date) : null;
      if (d && d.getHours() >= BUSINESS_START_HOUR) return exp.date; // hora real (POS)
      if (exp.dateStr) return exp.dateStr; // marcado en módulo Gastos a medianoche
      if (exp.date) return exp.date;
      if (exp.createdAt) return exp.createdAt;
      return exp.fecha;
    }

    // Gastos del módulo Gastos suelen guardarse a medianoche; usa dateStr para llevarlos al inicio del día comercial.
    if (exp?.date) {
      const d = toDateOrNull(exp.date);
      if (d && exp.dateStr && d.getHours() < BUSINESS_START_HOUR) {
        return exp.dateStr;
      }
      return exp.date;
    }

    // Otros campos comunes.
    return exp.dateStr || exp.createdAt || exp.fecha;
  };

  const isPayrollExpense = (exp) => {
    const category = String(exp?.category || '').trim().toLowerCase();
    return exp?.source === 'payroll' || !!exp?.payrollReceiptId || category === 'nomina';
  };

  const filteredExpenses = useMemo(
    () =>
      (expensesSource || []).filter((exp) => {
        const ref = expenseRef(exp);
        return isWithinBusinessRange(ref);
      }),
    [expensesSource, isWithinBusinessRange]
  );

  const standaloneExpenses = useMemo(
    () => filteredExpenses.filter((exp) => !exp.entradaRef),
    [filteredExpenses]
  );

  const payrollExpenses = useMemo(
    () => standaloneExpenses.filter((exp) => isPayrollExpense(exp)),
    [standaloneExpenses]
  );

  const otherExpenses = useMemo(
    () => standaloneExpenses.filter((exp) => !isPayrollExpense(exp)),
    [standaloneExpenses]
  );

  const filteredIncomingEntries = useMemo(
    () =>
      (incomingSource || []).filter((entry) => {
        const ref =
          entry.registrationDate || entry.date || entry.fecha || entry.timestamp;
        return isWithinBusinessRange(ref);
      }),
    [incomingSource, isWithinBusinessRange]
  );

  const purchasesPaid = useMemo(
    () =>
      filteredIncomingEntries
        .filter((entry) =>
          (entry.entryType || entry.tipo || '').toString().toLowerCase().includes('compra')
        )
        .filter(
          (entry) =>
            normalizePaid(entry.paymentStatus || entry.status || entry.state || entry.estadoPago) === 'paid'
        )
        .reduce((sum, entry) => sum + Number(entry.totalEntryCost ?? entry.totalEntrada ?? entry.total ?? 0), 0),
    [filteredIncomingEntries]
  );

  const purchasesPending = useMemo(
    () =>
      filteredIncomingEntries
        .filter((entry) =>
          (entry.entryType || entry.tipo || '').toString().toLowerCase().includes('compra')
        )
        .filter(
          (entry) =>
            normalizePaid(entry.paymentStatus || entry.status || entry.state || entry.estadoPago) === 'pending'
        )
        .reduce((sum, entry) => sum + Number(entry.totalEntryCost ?? entry.totalEntrada ?? entry.total ?? 0), 0),
    [filteredIncomingEntries]
  );

  const totalSales = useMemo(
    () => filteredSales.reduce((sum, sale) => sum + Number(sale.total || 0), 0),
    [filteredSales]
  );

  const totalCOGS = useMemo(() => {
    return filteredSales.reduce((sum, sale) => {
      const lines = Array.isArray(sale.products) ? sale.products : [];
      return sum + lines.reduce((acc, line) => acc + computeLineCost(line, productsById, productsByName), 0);
    }, 0);
  }, [filteredSales, productsById, productsByName]);

  const grossProfit = totalSales - totalCOGS;
  const payrollExpensesTotal = useMemo(
    () => payrollExpenses.reduce((sum, exp) => sum + Number(exp.amount ?? exp.monto ?? 0), 0),
    [payrollExpenses]
  );
  const otherExpensesTotal = useMemo(
    () => otherExpenses.reduce((sum, exp) => sum + Number(exp.amount ?? exp.monto ?? 0), 0),
    [otherExpenses]
  );
  const totalExpenses = otherExpensesTotal + payrollExpensesTotal;
  const netOperatingProfit = grossProfit - totalExpenses - purchasesPaid;

  const netCashFlow = totalSales - purchasesPaid - totalExpenses;

  const purchasesPeriod = purchasesPaid + purchasesPending;
  const purchasesPct = totalSales > 0 ? (purchasesPeriod / totalSales) * 100 : 0;

  const inventoryCurrentValue = useMemo(() => {
    const list = Array.isArray(productsData) ? productsData : [];
    return list.reduce(
      (sum, product) =>
        sum + Math.max(0, Number(product.stock || 0)) * Number(product.costPrice || 0),
      0
    );
  }, [productsData]);

  const inventoryTurnover = inventoryCurrentValue > 0 ? totalCOGS / inventoryCurrentValue : 0;
  const avgTicket = filteredSales.length ? totalSales / filteredSales.length : 0;
  const marginPct = totalSales > 0 ? (grossProfit / totalSales) * 100 : 0;
  const netMarginPct = totalSales > 0 ? (netOperatingProfit / totalSales) * 100 : 0;
  const costPct = totalSales > 0 ? (totalCOGS / totalSales) * 100 : 0;
  const expensesPct = totalSales > 0 ? (totalExpenses / totalSales) * 100 : 0;

  const statusCost = (v) => (v <= 35 ? ['Sano', 'green'] : v <= 45 ? ['Alerta', 'amber'] : ['Riesgo', 'red']);
  const statusExpenses = (v) => (v <= 25 ? ['Sano', 'green'] : v <= 35 ? ['Alerta', 'amber'] : ['Riesgo', 'red']);
  const statusPurchasesPct = (v) => {
    if (purchasesPeriod <= 0) {
      if (!totalSales || totalSales <= 0) return ['Neutro', 'gray'];
      if (rangeDays <= 2) return ['Neutro', 'gray'];
      return ['Alerta', 'amber'];
    }
    if (!totalSales || totalSales <= 0) {
      return rangeDays <= 2 ? ['Alerta', 'amber'] : ['Riesgo', 'red'];
    }
    if (v >= 70 && v <= 110) return ['Sano', 'green'];
    if ((v >= 50 && v < 70) || (v > 110 && v <= 140)) return ['Alerta', 'amber'];
    return ['Riesgo', 'red'];
  };
  const statusNetMargin = (v) => (v >= 8 ? ['Sano', 'green'] : v >= 5 ? ['Alerta', 'amber'] : ['Riesgo', 'red']);
  const statusInventoryTurn = (v) =>
    v >= 1 && v <= 2.5 ? ['Sano', 'green'] : (v >= 0.8 && v < 1) || (v > 2.5 && v <= 3)
      ? ['Alerta', 'amber']
      : ['Riesgo', 'red'];
  const statusCashFlow = (cash, sales) => {
    if (sales <= 0) return cash >= 0 ? ['Sano', 'green'] : ['Riesgo', 'red'];
    const floor = -0.05 * sales;
    return cash >= 0 ? ['Sano', 'green'] : cash >= floor ? ['Alerta', 'amber'] : ['Riesgo', 'red'];
  };

  const filteredOutgoing = useMemo(
    () =>
      (outgoingData || []).filter((out) => {
        const ref = out.registrationDate || out.fecha || out.date || out.createdAt;
        return isWithinBusinessRange(ref);
      }),
    [outgoingData, isWithinBusinessRange]
  );

  return (
    <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-800 transition duration-200 px-4 py-2 rounded-lg border border-blue-200 hover:border-blue-400"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver al Menú
        </button>
        <h1 className="text-4xl font-extrabold text-gray-900 drop-shadow-sm">Panel Financiero</h1>
        <div className="w-32" />
      </div>

      {panelLoading && (
        <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-blue-800">
          Calculando información completa del periodo… Estamos cargando todas las páginas de ventas, entradas y gastos.
        </div>
      )}
      {panelError && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-800">
          No se pudo completar toda la información del periodo. Revisa la conexión e inténtalo nuevamente antes de usar estos totales.
        </div>
      )}
      {!productsLoading && catalogIncomplete && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
          El catálogo de productos todavía no está confirmado como completo. Los costos y la utilidad de productos con receta pueden cambiar cuando termine la sincronización.
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Filtros</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-4 py-2 border rounded"
            >
              <option value="today">Hoy</option>
              <option value="specific">Fecha específica</option>
              <option value="range">Rango</option>
            </select>
          </div>
          {filterType === 'specific' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
              <input
                type="date"
                value={specificDate}
                onChange={(e) => setSpecificDate(e.target.value)}
                className="w-full px-4 py-2 border rounded"
              />
            </div>
          )}
          {filterType === 'range' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
                <input
                  type="date"
                  value={rangeStart}
                  onChange={(e) => setRangeStart(e.target.value)}
                  className="w-full px-4 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
                <input
                  type="date"
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                  className="w-full px-4 py-2 border rounded"
                />
              </div>
            </>
          )}
          <div className="text-sm text-gray-600 md:col-span-1">
            <div>Rango efectivo:</div>
            <div className="font-semibold">
              {startDate} 08:00 → {endDate} (+1d) 07:50
            </div>
            <div className="text-xs text-gray-500">Día comercial: 08:00 AM a 07:50 AM del día siguiente.</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <h3 className="text-lg font-medium text-gray-600 mb-2">
            Total de Ventas <Hint text="Suma de venta.total en el rango." />
          </h3>
          <p className="text-4xl font-extrabold text-blue-600">{fmtCOP(totalSales)}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <h3 className="text-lg font-medium text-gray-600 mb-2">
            Costos de las Ventas (COGS) <Hint text="Costo directo de los productos vendidos." />
          </h3>
          <p className="text-4xl font-extrabold text-red-600">{fmtCOP(totalCOGS)}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <h3 className="text-lg font-medium text-gray-600 mb-2">Utilidad Bruta</h3>
          <p className="text-4xl font-extrabold text-green-600">{fmtCOP(grossProfit)}</p>
          <div className="mt-1">
            {(() => {
              const [lbl, col] = statusNetMargin(marginPct);
              return <StatusPill label={lbl} color={col} />;
            })()}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <h3 className="text-lg font-medium text-gray-600 mb-2">Gastos (sin nomina)</h3>
          <p className="text-4xl font-extrabold text-purple-600">{fmtCOP(otherExpensesTotal)}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <h3 className="text-lg font-medium text-gray-600 mb-2">Gastos de nomina</h3>
          <p className="text-4xl font-extrabold text-indigo-600">{fmtCOP(payrollExpensesTotal)}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <h3 className="text-lg font-medium text-gray-600 mb-2">
            Compras Pagadas <Hint text="Entradas de compra registradas como pagadas." />
          </h3>
          <p className="text-4xl font-extrabold text-emerald-600">{fmtCOP(purchasesPaid)}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <h3 className="text-lg font-medium text-gray-600 mb-2">
            Compras Pendientes por Pagar <Hint text="Compras de inventario con estado pendiente." />
          </h3>
          <p className="text-4xl font-extrabold text-amber-600">{fmtCOP(purchasesPending)}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <h3 className="text-lg font-medium text-gray-600 mb-2">Ganancia Neta Disponible</h3>
          <p className="text-4xl font-extrabold text-teal-600">{fmtCOP(netOperatingProfit)}</p>
          <p className="text-sm text-gray-500 mt-1">% Utilidad bruta / ventas: {marginPct.toFixed(2)}%</p>
          <p className="text-sm text-gray-500">% Ganancia neta / ventas: {netMarginPct.toFixed(2)}%</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <h3 className="text-lg font-medium text-gray-600 mb-2">
            % Costos sobre Ventas <Hint text="(COGS / Ventas) × 100" />
          </h3>
          <p className="text-4xl font-extrabold text-gray-900">{costPct.toFixed(2)}%</p>
          <div className="mt-1">
            {(() => {
              const [lbl, col] = statusCost(costPct);
              return <StatusPill label={lbl} color={col} />;
            })()}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <h3 className="text-lg font-medium text-gray-600 mb-2">
            % Gastos sobre Ventas <Hint text="(Gastos totales / Ventas) × 100" />
          </h3>
          <p className="text-4xl font-extrabold text-gray-900">{expensesPct.toFixed(2)}%</p>
          <div className="mt-1">
            {(() => {
              const [lbl, col] = statusExpenses(expensesPct);
              return <StatusPill label={lbl} color={col} />;
            })()}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <h3 className="text-lg font-medium text-gray-600 mb-2">
            Compras del Período <Hint text="Compras pagadas + pendientes en el rango." />
          </h3>
          <p className="text-4xl font-extrabold text-indigo-600">{fmtCOP(purchasesPeriod)}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <h3 className="text-lg font-medium text-gray-600 mb-2">
            % Compras / Ventas <Hint text="(Compras del período / Ventas) × 100" />
          </h3>
          <p className="text-4xl font-extrabold text-gray-900">{purchasesPct.toFixed(2)}%</p>
          <div className="mt-1">
            {(() => {
              const [lbl, col] = statusPurchasesPct(purchasesPct);
              return <StatusPill label={lbl} color={col} />;
            })()}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <h3 className="text-lg font-medium text-gray-600 mb-2">
            Flujo de Caja Neto <Hint text="Ventas − Compras pagadas − Gastos" />
          </h3>
          <p className="text-4xl font-extrabold text-teal-700">{fmtCOP(netCashFlow)}</p>
          <div className="mt-1">
            {(() => {
              const [lbl, col] = statusCashFlow(netCashFlow, totalSales);
              return <StatusPill label={lbl} color={col} />;
            })()}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <h3 className="text-lg font-medium text-gray-600 mb-2">
            Rotación de Inventario (aprox.) <Hint text="COGS / Inventario actual (costo)" />
          </h3>
          <p className="text-4xl font-extrabold text-gray-900">{inventoryTurnover.toFixed(2)}x</p>
          <div className="mt-1">
            {(() => {
              const [lbl, col] = statusInventoryTurn(inventoryTurnover);
              return <StatusPill label={lbl} color={col} />;
            })()}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg text-center md:col-span-3">
          <h3 className="text-lg font-medium text-gray-600 mb-2">Ticket Promedio</h3>
          <p className="text-3xl font-extrabold text-gray-900">{fmtCOP(avgTicket)}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Explicación de Indicadores Clave de Desempeño (ICD)</h2>
        <p className="text-sm text-gray-600 mb-6">
          Aquí se explica qué mide cada indicador, cómo se calcula y para qué sirve dentro del negocio.
        </p>
        <div className="space-y-6 text-sm text-gray-700 leading-relaxed">
          <div>
            <p className="font-semibold text-gray-900">Total de Ventas</p>
            <p className="text-xs text-gray-500 mb-1">Fórmula: Suma de venta.total en el periodo.</p>
            <p>¿Qué significa? Es el ingreso total generado por todas las ventas.</p>
            <p>¿Para qué sirve? Para evaluar el desempeño general, comparar periodos y medir crecimiento.</p>
          </div>
          <div>
            <p className="font-semibold text-gray-900">Costos de las Ventas (COGS)</p>
            <p className="text-xs text-gray-500 mb-1">Fórmula: Suma del costo por producto vendido.</p>
            <p>¿Qué significa? Representa el costo directo de los productos vendidos.</p>
            <p>¿Para qué sirve? Para conocer la rentabilidad real y calcular la utilidad bruta.</p>
          </div>
          <div>
            <p className="font-semibold text-gray-900">Utilidad Bruta</p>
            <p className="text-xs text-gray-500 mb-1">Fórmula: Ventas − COGS.</p>
            <p>¿Qué significa? Es lo que queda después de cubrir el costo directo del producto.</p>
            <p>¿Para qué sirve? Para evaluar si los precios y márgenes son adecuados.</p>
          </div>
          <div>
            <p className="font-semibold text-gray-900">Gastos (sin nomina)</p>
            <p className="text-xs text-gray-500 mb-1">Fórmula: Suma de gasto.amount sin categoria Nomina.</p>
            <p>¿Qué significa? Representa los gastos operativos sin incluir nomina.</p>
            <p>¿Para qué sirve? Para controlar costos, detectar fugas y mejorar eficiencia.</p>
          </div>
          <div>
            <p className="font-semibold text-gray-900">Gastos de nomina</p>
            <p className="text-xs text-gray-500 mb-1">Fórmula: Suma de gasto.amount con categoria Nomina.</p>
            <p>¿Qué significa? Gastos creados desde Nómina (pagos a empleados).</p>
            <p>¿Para qué sirve? Separar nomina de otros gastos operativos.</p>
          </div>
          <div>
            <p className="font-semibold text-gray-900">Compras Pagadas</p>
            <p className="text-xs text-gray-500 mb-1">Fórmula: Suma de compra.totalEntryCost con estado “pagado”.</p>
            <p>¿Qué significa? Son compras de inventario ya liquidadas.</p>
            <p>¿Para qué sirve? Para gestionar el flujo de caja y planificar pagos.</p>
          </div>
          <div>
            <p className="font-semibold text-gray-900">Compras Pendientes por Pagar</p>
            <p className="text-xs text-gray-500 mb-1">Fórmula: Suma de compra.totalEntryCost con estado “pendiente”.</p>
            <p>¿Qué significa? Son compras que aún no se han pagado.</p>
            <p>¿Para qué sirve? Para anticipar obligaciones futuras y evitar falta de efectivo.</p>
          </div>
          <div>
            <p className="font-semibold text-gray-900">Ganancia Neta Disponible</p>
            <p className="text-xs text-gray-500 mb-1">Formula: Utilidad Bruta - Gastos totales (operativos + nomina).</p>
            <p>¿Qué significa? Es la ganancia real después de todos los costos y gastos.</p>
            <p>¿Para qué sirve? Muestra cuánto dinero queda para reinvertir o retirar.</p>
          </div>
          <div>
            <p className="font-semibold text-gray-900">% Utilidad Bruta / Ventas</p>
            <p className="text-xs text-gray-500 mb-1">Fórmula: (Utilidad Bruta ÷ Ventas) × 100.</p>
            <p>¿Qué significa? Indica qué porcentaje de tus ventas es ganancia bruta.</p>
            <p>¿Para qué sirve? Para medir qué tan rentable es tu operación.</p>
          </div>
          <div>
            <p className="font-semibold text-gray-900">% Ganancia Neta / Ventas</p>
            <p className="text-xs text-gray-500 mb-1">Fórmula: (Ganancia Neta ÷ Ventas) × 100.</p>
            <p>¿Qué significa? Mide la ganancia final en relación a las ventas.</p>
            <p>¿Para qué sirve? Para evaluar la rentabilidad general del negocio.</p>
          </div>
          <div>
            <p className="font-semibold text-gray-900">Ticket Promedio</p>
            <p className="text-xs text-gray-500 mb-1">Fórmula: Ventas ÷ Número de ventas.</p>
            <p>¿Qué significa? Valor promedio que gasta un cliente por compra.</p>
            <p>¿Para qué sirve? Para estrategias de aumento de venta (upselling / cross-selling).</p>
          </div>
          <div>
            <p className="font-semibold text-gray-900">% Costos/Ventas</p>
            <p className="text-xs text-gray-500 mb-1">Fórmula: (COGS ÷ Ventas) × 100.</p>
            <p>¿Qué significa? Indica qué parte de las ventas se va en costo del producto.</p>
            <p>¿Para qué sirve? Mientras más bajo, mejor margen de ganancia.</p>
          </div>
          <div>
            <p className="font-semibold text-gray-900">% Gastos/Ventas</p>
            <p className="text-xs text-gray-500 mb-1">Fórmula: (Gastos ÷ Ventas) × 100.</p>
            <p>¿Qué significa? Mide cuánto representan los gastos sobre tus ingresos.</p>
            <p>¿Para qué sirve? Para controlar costos operativos y buscar eficiencia.</p>
          </div>
          <div>
            <p className="font-semibold text-gray-900">Compras del Período</p>
            <p className="text-xs text-gray-500 mb-1">Fórmula: Compras pagadas + compras pendientes del rango.</p>
            <p>¿Qué significa? Total comprado en un periodo (pagado o no).</p>
            <p>¿Para qué sirve? Para analizar reposición e inversión en inventario.</p>
          </div>
          <div>
            <p className="font-semibold text-gray-900">% Compras/Ventas</p>
            <p className="text-xs text-gray-500 mb-1">Fórmula: (Compras del período ÷ Ventas) × 100.</p>
            <p>¿Qué significa? Relación entre compras e ingresos.</p>
            <p>¿Para qué sirve? Detecta si estás comprando demasiado o demasiado poco.</p>
          </div>
          <div>
            <p className="font-semibold text-gray-900">Flujo de Caja Neto</p>
            <p className="text-xs text-gray-500 mb-1">Fórmula: Ventas – Compras Pagadas – Gastos.</p>
            <p>¿Qué significa? Mide cuánto efectivo real generó el negocio.</p>
            <p>¿Para qué sirve? Para tomar decisiones de pagos, retiros, compras o inversiones.</p>
          </div>
          <div>
            <p className="font-semibold text-gray-900">Rotación de Inventario (aprox.)</p>
            <p className="text-xs text-gray-500 mb-1">Fórmula: COGS ÷ Inventario actual (ideal: inventario promedio).</p>
            <p>¿Qué significa? Cuántas veces vendes y repones tu inventario.</p>
            <p>¿Para qué sirve? Para evaluar si el inventario se mueve bien o se queda estancado.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
